import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';

type Joke = { setup: string; punchline: string };

// How long the toast stays fully visible once a joke has loaded.
const VISIBLE_MS = 5000;
// Grace period after the pointer leaves the toast before it hides.
const LEAVE_MS = 1500;

// Ephemeral "random joke" toast: fetches one joke on mount, slides in at the
// bottom right of the home content, and hides after VISIBLE_MS. Hovering it
// pauses the timer so the joke stays readable. Rendered inside <Content> as a
// sticky overlay, so it never shifts layout nor covers the sidebar.
export const EphemeralJokeBanner = () => {
  const [joke, setJoke] = useState<Joke | undefined>();
  const [open, setOpen] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();

  const scheduleHide = (ms: number) => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setOpen(false), ms);
  };

  useEffect(() => {
    let cancelled = false;

    fetch('https://official-joke-api.appspot.com/jokes/programming/random')
      .then(res => res.json())
      .then((data: Joke | Joke[]) => {
        if (cancelled) return;
        const next = Array.isArray(data) ? data[0] : data;
        if (!next?.setup) return;
        setJoke(next);
        setOpen(true);
        scheduleHide(VISIBLE_MS);
      })
      .catch(() => {
        // Joke API unreachable — silently skip the toast.
      });

    return () => {
      cancelled = true;
      clearTimeout(hideTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!joke) return null;

  return (
    <Slide in={open} direction="up" timeout={400} unmountOnExit appear>
      <Box
        role="status"
        onMouseEnter={() => clearTimeout(hideTimer.current)}
        onMouseLeave={() => scheduleHide(LEAVE_MS)}
        sx={{
          position: 'sticky',
          bottom: 16,
          zIndex: theme => theme.zIndex.snackbar,
          width: 'max-content',
          maxWidth: 'calc(100% - 48px)',
          ml: 'auto',
          mr: { xs: 'auto', md: 3 },
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: '30px',
          boxShadow: 6,
          px: 2.25,
          py: 1.25,
        }}
      >
        <EmojiEmotionsIcon sx={{ fontSize: 17, color: 'primary.main' }} />
        <Typography component="span" variant="body2">
          <Box component="strong" sx={{ fontWeight: 600 }}>
            {joke.setup}
          </Box>{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {joke.punchline}
          </Box>
        </Typography>
      </Box>
    </Slide>
  );
};
